import React, { useContext, useState, useEffect } from 'react';
import PurpleHat from '../../assets/images/project/purple-hat.svg';
import CoffeeChat from '../../assets/svg/coffee-chat.svg';
import { MessageContext } from '../../contexts/messageContext';
import CoffeeChatModal from '../Messages/CoffeeChatModal';

interface MatchCardProps {
  userName: string | undefined;
  userLocation: string | undefined;
  userBio: string | undefined;
  userAvatar: string | undefined;
  onStartChat: () => void;
  onMaybeLater: () => void;
  user?: any;
}

/**
 * MatchCard Component for Coffee Chat Feature
 *
 * Testing with specific user IDs:
 * - Add ?testUserId=123 to the URL to test with a specific user ID
 * - Example: http://localhost:3001/match?testUserId=379
 * - This will override the actual user ID for testing purposes
 *
 * Production usage:
 * - Uses the actual user.id from props
 * - No URL parameters needed in production
 */
const MatchCard: React.FC<MatchCardProps> = ({
  userName,
  userLocation,
  userBio,
  userAvatar,
  onStartChat,
  onMaybeLater,
  user
}) => {
  const { actions } = useContext(MessageContext);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showCoffeeChatModal, setShowCoffeeChatModal] = useState<boolean>(false);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);

  // Check if we're in test mode
  const urlParams = new URLSearchParams(window.location.search);
  const testUserId = urlParams.get('testUserId');
  const isTestMode = !!testUserId;

  // Log testing instructions on component mount
  useEffect(() => {
    if (isTestMode) {
      console.log(`
🧪 COFFEE CHAT TEST MODE ACTIVE 🧪
===============================
Current test user ID: ${testUserId}
Target user: ${userName ?? 'Unknown'}
Context: ${window.location.pathname.includes('/project/') ? 'Project Page' : 'Match Page'}

Test user will override: ${user?.id || 'No user ID'} → ${testUserId}
      `);
    }
  }, [isTestMode, testUserId, userName, user?.id]);

  const handleStartChat = () => {
    setShowCoffeeChatModal(true);
  };

  const getTargetUserId = () => {
    // Check for test parameter in URL for development/testing purposes
    const urlParams = new URLSearchParams(window.location.search);
    const testUserId = urlParams.get('testUserId');

    if (testUserId) {
      console.log('🧪 [TESTING] Using test user ID from URL parameter:', testUserId);
      return Number(testUserId);
    }

    // Use the actual user ID from props
    if (user?.id) {
      console.log('✅ Using actual user ID from props:', user.id);
      return Number(user.id);
    }

    // Fallback error case
    console.error('❌ No user ID available for coffee chat creation');
    return null;
  };

  const handleAcceptCoffeeChat = async () => {
    if (!actions) {
      console.error('❌ Actions not available');
      return;
    }

    const targetUserId = getTargetUserId();
    if (!targetUserId) {
      console.error('❌ Cannot create coffee chat: No target user ID available');
      alert('Error: Unable to identify the user to chat with. Please try again.');
      return;
    }

    setIsRequesting(true);
    try {
      console.log('☕ Creating coffee chat room with user ID:', targetUserId);
      console.log('👤 Target user details:', {
        name: userName,
        location: userLocation,
        id: targetUserId
      });

      // Create a coffee chat room and send the first message
      await actions.handleCreateCoffeeChatRoom([targetUserId]);

      console.log('✅ Coffee chat room created successfully');
      setShowCoffeeChatModal(false);
      onStartChat();
    } catch (error) {
      console.error('❌ Error creating coffee chat room:', error);
      alert('Failed to create coffee chat. Please try again.');
    } finally {
      setIsRequesting(false);
    }
  };

  const toggleBio = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMaybeLater = () => {
    setShowCoffeeChatModal(false);
    onMaybeLater();
  };

  return (
    <>
      {/* Test Mode Indicator */}
      {isTestMode && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: '#ff6b6b',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          🧪 TEST MODE: User ID {testUserId}
        </div>
      )}

      <CoffeeChatModal
        isOpen={showCoffeeChatModal}
        onClose={() => setShowCoffeeChatModal(false)}
        matchName={userName ?? ''}
        matchAvatar={userAvatar ?? ''}
        matchLocation={userLocation}
        matchBio={userBio}
        onAccept={handleAcceptCoffeeChat}
        onRequestAnother={handleMaybeLater}
        mode="request"
        isLoading={isRequesting}
      />

      <div className="match-card">
        <div className="match-card-content">
          <div className="match-card-hat">
            <img src={PurpleHat} alt="Purple hat" className="match-hat-image" />
            <span className="match-hat-sparkle left"></span>
            <span className="match-hat-sparkle right"></span>
          </div>
          <h2 className="match-title">
            "It's a Match! 🎉"
          </h2>
          <p className="match-description">
            Matchi worked hard, and voilà—a perfect match for your next Coffee Chat!
          </p>
          <div className="match-user">
            <img src={userAvatar} alt={userName} className="match-user-avatar" />
            <div className="match-user-info">
              <h3 className="match-user-name">{userName}</h3>
              <p className="match-user-location">
                {userLocation} • {
                  isExpanded
                    ? userBio
                    : `${userBio?.substring(0, 30)}...`
                }
                <span
                  className="show-more-link"
                  onClick={toggleBio}
                  style={{ cursor: 'pointer', color: '#4a4a4a', marginLeft: '4px' }}
                >
                  {isExpanded ? 'Show Less' : 'Show More'}
                </span>
              </p>
            </div>
          </div>
          <div className="match-actions">
            <button
              className="button is-primary is-fullwidth match-chat-button"
              onClick={handleStartChat}
            >
              <span className="icon">
                <img
                  src={CoffeeChat}
                  alt="Chat icon"
                  className="match-chat-icon"/>
              </span>
              <span>Start a Coffee Chat</span>
            </button>
            <button
              className="button is-text is-fullwidth match-later-button"
              onClick={handleMaybeLater}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchCard;